'use client';

import React from 'react';
import { IconArrowDownRight, IconChatAnswer, IconChatQuestion, IconReply } from '@repo/svgs';
import { Divider, LinkCell } from '@/components/ui';
import { ListCell } from '@/components/ui/ListCell';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { AnswerQuestionModal } from './modals/AnswerQuestionModal';
import type { SeoQuestion } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';

interface QuestionCardProps {
  question: SeoQuestion;
  onAnswer?: () => void;
  enableModalTrigger?: boolean;
  eventScreen?: 'building' | 'contact';
}

export function QuestionCard({
  question,
  onAnswer,
  enableModalTrigger = false,
  eventScreen = 'building',
}: QuestionCardProps) {
  const analytics = useAnalytics();
  const screen = eventScreen;
  const answerTrigger = enableModalTrigger ? (
    <ModalTrigger
      label="Answer a question"
      content={<AnswerQuestionModal questionId={question.id} questionText={question.question} />}
      onOpen={() => analytics.track(`${screen}_answer_question`)}
    >
      <LinkCell icon={IconReply} label="Answer" />
    </ModalTrigger>
  ) : (
    <LinkCell
      icon={IconReply}
      label="Answer"
      onClick={() => {
        analytics.track(`${screen}_answer_question`);
        onAnswer?.();
      }}
    />
  );

  return (
    <div className="border border-secondary p-2 rounded-2xl flex flex-col gap-1 w-full">
      {/* Question */}
      <ListCell
        title={question.question}
        subtitle={question.createdTime}
        className="hover:!bg-white !cursor-default"
        titleClassName="text-body-2-high"
        trailing="none"
      />

      {/* Answers */}
      {question.answers.map((answer, index) => (
        <React.Fragment key={index}>
          <ListCell
            leadingIcon={<IconArrowDownRight className="w-6 h-6 icon-secondary" />}
            title={answer.answer}
            subtitle={answer.createdTime}
            trailing="none"
            className="hover:!bg-white !cursor-default"
          />
        </React.Fragment>
      ))}

      {/* Answer Link */}
      <Divider variant="tertiary" className="px-2" />
      {answerTrigger}
    </div>
  );
}

export default QuestionCard;
